// Repo detail / artifact browser.
// Drill-down from /repos — shows the repo card, then a paginated
// list of artifacts (path, version, size, sha256, uploaded_at).
//
// v124 — repo properties are editable in place. Click "Edit" on the
// header to swap the read-only summary for a form; save calls the
// PATCH /api/v1/repos/{id} endpoint and re-fetches the SWR cache.
// Slug, kind, format, upstreamUrl, name, and (for virtuals) members
// are all editable. id and projectId are not.
//
// v186 — project-owner-editable retention card. Mounts under the
// repo header and talks to /api/v1/repos/{id}/retention. The
// endpoint 403s for non-maintainers; the card hides itself in that
// case so non-owners don't see an empty edit form they can't use.
//
// v190 — per-repo Stats tab (roadmap item 14). The tab switcher
// lives at the page top once a repo loads; "Contents" keeps the
// existing artifact table + retention card surface, "Stats" loads
// the 30-day pull / storage / consumer rollup. Tab state is in
// `?tab=stats` so deep links stay shareable. Stats data comes from
// /api/v1/repos/{id}/stats; the principal_email columns are only
// populated for maintainer+ callers (backend gates this) and the
// "Top consumers" tile collapses cleanly when empty.

import { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BarChart3, Box, Calendar, ChevronDown, ChevronRight, Copy, Download, FolderOpen, GitBranch, Pencil, ShieldCheck, Trash2, X } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import {
  createRepoRetention,
  deleteRepo,
  deleteRepoRetention,
  updateRepo,
  updateRepoRetention,
  useProjects,
  useRepoArtifacts,
  useRepoDetail,
  useRepoResolutionCoordinates,
  useRepoResolutionStats,
  useRepoRetention,
  useRepoSafeVersion,
  setRepoSafeVersion,
  type SafeVersionMode,
  useRepoStats,
  useRepos,
  type Repo,
  type RepoPatch,
  type RepoResolutionStatsMember,
  type RepoStats,
  type RetentionPolicy,
  useFormatCapabilities,
} from '../api/client';
import {
  type DockerImageSummary,
  type DockerImageTag,
  useDockerImages,
  useDockerImagesPaginated,
  useDockerImageTagsPaginated,
} from '../api/docker';
import { FORMATS, kindsForFormat } from '../lib/formats';
import { MemberSelector } from '../components/MemberSelector';
import { RepoReleaseHoldCard } from './RepoReleaseHoldCard';
import { RepoSigningKeyCard } from './RepoSigningKeyCard';

const KINDS: Repo['kind'][] = ['local', 'remote', 'virtual'];
const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function kindTone(kind: Repo['kind']): 'accent' | 'nebula' | 'neutral' {
  if (kind === 'remote') return 'nebula';
  if (kind === 'virtual') return 'accent';
  return 'neutral';
}

interface EditFormProps {
  readonly repo: Repo;
  readonly onSaved: (next: Repo) => void;
  readonly onCancel: () => void;
}

function EditForm({ repo, onSaved, onCancel }: EditFormProps) {
  const { t } = useTranslation();
  // Local form state seeded from the loaded repo. We send the whole
  // surface to PATCH on save (the backend will no-op fields that
  // didn't change). Members come back as undefined for non-virtuals;
  // normalise to [] so the MemberSelector has a stable input.
  const [draft, setDraft] = useState<Repo>({
    ...repo,
    members: repo.members ?? [],
    upstreamUrl: repo.upstreamUrl ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: capData } = useFormatCapabilities();
  // Offer only kinds the chosen format supports (item 416). The repo's
  // stored kind stays listed so an existing, now-unsupported repo is
  // shown truthfully instead of silently snapping to another kind.
  const editKinds = kindsForFormat(KINDS, draft.format, capData?.formats);
  if (!editKinds.includes(repo.kind) && draft.format === repo.format) editKinds.push(repo.kind);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      // Send only the editable fields. id + projectId never move; the
      // backend rejects them silently. Strip members on non-virtual
      // kinds so the backend's "members on non-virtual" guard is
      // never triggered by stale state.
      const patch: RepoPatch = {
        slug: draft.slug,
        name: draft.name,
        kind: draft.kind,
        format: draft.format,
        upstreamUrl: draft.upstreamUrl ?? '',
        members: draft.kind === 'virtual' ? draft.members ?? [] : [],
      };
      const next = await updateRepo(repo.id, patch);
      onSaved(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader title={t('repoDetail.edit.title')} description={t('repoDetail.edit.description')} />
      <CardBody>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">{t('repoDetail.edit.slugLabel')}</span>
            <Input
              value={draft.slug}
              onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">{t('repoDetail.edit.nameLabel')}</span>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              required
            />
          </label>
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">{t('repoDetail.edit.kindLabel')}</span>
            <select
              className={selectClass + ' w-full'}
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value as Repo['kind'] })}
            >
              {editKinds.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
          <label className="space-y-1">
            <span className="orb-section-title text-fg-muted">{t('repoDetail.edit.formatLabel')}</span>
            <select
              className={selectClass + ' w-full'}
              value={draft.format}
              onChange={(e) => {
                const format = e.target.value as Repo['format'];
                const ok = kindsForFormat(KINDS, format, capData?.formats);
                setDraft({ ...draft, format, kind: ok.includes(draft.kind) ? draft.kind : 'local' });
              }}
            >
              {FORMATS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1 md:col-span-2">
            <span className="orb-section-title text-fg-muted">
              {t('repoDetail.edit.upstreamLabel')} {draft.kind !== 'remote' && (
                <span className="text-fg-subtle">{t('repoDetail.edit.upstreamHint', { kind: draft.kind })}</span>
              )}
            </span>
            <Input
              value={draft.upstreamUrl ?? ''}
              onChange={(e) => setDraft({ ...draft, upstreamUrl: e.target.value })}
              placeholder={t('repoDetail.edit.upstreamPlaceholder')}
            />
          </label>

          {draft.kind === 'virtual' && (
            <div className="md:col-span-2 space-y-2">
              <p className="orb-section-title text-fg-muted">{t('repoDetail.edit.membersTitle')}</p>
              <p className="text-xs text-fg-muted">
                {t('repoDetail.edit.membersHint')}
              </p>
              <MemberSelector
                projectId={draft.projectId}
                format={draft.format}
                excludeId={repo.id}
                value={draft.members ?? []}
                onChange={(members) => setDraft({ ...draft, members })}
              />
            </div>
          )}

          {err && (
            <p className="md:col-span-2 text-sm text-danger font-mono">{err}</p>
          )}

          <div className="md:col-span-2 flex items-center gap-2 pt-2">
            <Button variant="primary" type="submit" disabled={busy}>
              {t('repoDetail.edit.save')}
            </Button>
            <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
              <X className="h-4 w-4" />
              {t('repoDetail.edit.cancel')}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

interface ReadOnlyHeaderProps {
  readonly repo: Repo;
  readonly onEdit: () => void;
  readonly onTrash: () => void;
}

function ReadOnlyHeader({ repo, onEdit, onTrash }: ReadOnlyHeaderProps) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="orb-eyebrow text-accent/80 mb-2">{t('repoDetail.eyebrow')}</p>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-accent/10 border border-accent/30 flex items-center justify-center shrink-0">
          <Box className="h-5 w-5 text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-display font-semibold text-fg leading-tight">{repo.name}</h1>
            <Badge tone={kindTone(repo.kind)}>{repo.kind}</Badge>
            <span className="orb-code text-xs">{repo.format}</span>
          </div>
          <p className="text-fg-muted text-sm mt-2 font-mono">{repo.slug}</p>
          {repo.capabilityWarning && (
            <p role="alert" data-testid="capability-warning" className="text-warning text-xs mt-2">
              {repo.capabilityWarning}
            </p>
          )}
          {repo.upstreamUrl && (
            <p className="text-fg-muted text-xs mt-1 truncate">
              {t('repoDetail.header.upstream')} <span className="text-fg">{repo.upstreamUrl}</span>
            </p>
          )}
          {repo.kind === 'virtual' && (
            <p className="text-fg-muted text-xs mt-1">
              {t('repoDetail.header.members')} <span className="text-fg">{repo.members?.length ?? 0}</span>
              {repo.members && repo.members.length > 0 && (
                <span className="text-fg-subtle"> {t('repoDetail.header.membersResolved')}</span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" type="button" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            {t('repoDetail.header.editButton')}
          </Button>
          <Button
            variant="ghost"
            type="button"
            onClick={onTrash}
            className="text-fg-muted hover:bg-danger/10 hover:text-danger"
            title={t('repoDetail.header.trashTitle')}
          >
            <Trash2 className="h-4 w-4" />
            {t('repoDetail.header.trashButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// SafeVersionCard — roadmap item 406 Phase A. Opt-in per virtual repo:
// whether blocked versions are remediated to the next clean one. The
// admin endpoint 403s for non-admins, in which case the card is hidden.
function SafeVersionCard({ repoID }: { readonly repoID: string }) {
  const { t } = useTranslation();
  const { data, error, mutate } = useRepoSafeVersion(repoID);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (error || !data) return null;

  const onChange = async (mode: SafeVersionMode) => {
    setBusy(true);
    setErr(null);
    try {
      await setRepoSafeVersion(repoID, mode);
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('repoDetail.safeVersion.updateFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('repoDetail.safeVersion.title')}
        description={t('repoDetail.safeVersion.description')}
      />
      <CardBody>
        <label className="text-sm" htmlFor="safe-version-mode">
          {t('repoDetail.safeVersion.mode')}{' '}
        </label>
        <select
          id="safe-version-mode"
          value={data.mode}
          disabled={busy}
          onChange={(e) => void onChange(e.target.value as SafeVersionMode)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="off">{t('repoDetail.safeVersion.off')}</option>
          <option value="patch">{t('repoDetail.safeVersion.patch')}</option>
          <option value="minor">{t('repoDetail.safeVersion.minor')}</option>
        </select>
        {err && <p className="mt-2 text-sm text-red-600">{err}</p>}
      </CardBody>
    </Card>
  );
}

// RetentionCard — v186. Lists retention policies for the repo and
// renders a small create-form below. The endpoint returns 403 for
// non-maintainers; in that case SWR's `error` is set and we hide the
// card entirely so non-owners aren't confused by a button that 403s.
//
// Validation matches the backend:
//   - At least one of max_age_days / keep_per_coord must be > 0.
//   - Both must be positive integers.
// We do client-side first-pass; the server is the authority.
function RetentionCard({ repoID }: { readonly repoID: string }) {
  const { t } = useTranslation();
  const { data, error, mutate } = useRepoRetention(repoID);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    max_age_days: string;
    keep_per_coord: string;
    enabled: boolean;
    dry_run: boolean;
  }>({ name: '', max_age_days: '', keep_per_coord: '', enabled: true, dry_run: true });

  // 403 → user is not a maintainer of this project. Hide the card so
  // we don't show a permission-denied for a feature they can't use.
  if (error) return null;

  const togglePolicy = async (p: RetentionPolicy, next: Partial<RetentionPolicy>) => {
    setBusy(true);
    setFormErr(null);
    try {
      const merged: RetentionPolicy = { ...p, ...next, rule: p.rule };
      await updateRepoRetention(repoID, p.name, merged);
      await mutate();
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : t('repoDetail.retention.updateFailed'));
    } finally {
      setBusy(false);
    }
  };

  const removePolicy = async (p: RetentionPolicy) => {
    if (!confirm(t('repoDetail.retention.confirmDelete', { name: p.name }))) return;
    setBusy(true);
    setFormErr(null);
    try {
      await deleteRepoRetention(repoID, p.name);
      await mutate();
    } catch (e) {
      setFormErr(e instanceof Error ? e.message : t('repoDetail.retention.deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErr(null);
    const maxAge = draft.max_age_days.trim() ? Number.parseInt(draft.max_age_days, 10) : undefined;
    const keep = draft.keep_per_coord.trim() ? Number.parseInt(draft.keep_per_coord, 10) : undefined;
    if (!draft.name.trim()) {
      setFormErr(t('repoDetail.retention.nameRequired'));
      return;
    }
    if (maxAge === undefined && keep === undefined) {
      setFormErr(t('repoDetail.retention.specifyOne'));
      return;
    }
    if (maxAge !== undefined && (!Number.isFinite(maxAge) || maxAge <= 0)) {
      setFormErr(t('repoDetail.retention.maxAgeInvalid'));
      return;
    }
    if (keep !== undefined && (!Number.isFinite(keep) || keep <= 0)) {
      setFormErr(t('repoDetail.retention.keepInvalid'));
      return;
    }
    setBusy(true);
    try {
      await createRepoRetention(repoID, {
        name: draft.name.trim(),
        rule: {
          ...(maxAge === undefined ? {} : { max_age_days: maxAge }),
          ...(keep === undefined ? {} : { keep_per_coord: keep }),
        },
        enabled: draft.enabled,
        dry_run: draft.dry_run,
      });
      setDraft({ name: '', max_age_days: '', keep_per_coord: '', enabled: true, dry_run: true });
      await mutate();
    } catch (err) {
      setFormErr(err instanceof Error ? err.message : t('repoDetail.retention.createFailed'));
    } finally {
      setBusy(false);
    }
  };

  const policies = data ?? [];

  return (
    <Card>
      <CardHeader
        title={t('repoDetail.retention.title')}
        description={t('repoDetail.retention.description')}
      />
      <CardBody>
        {policies.length > 0 ? (
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-3 py-2 font-medium text-fg-muted">{t('repoDetail.retention.name')}</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">{t('repoDetail.retention.maxAge')}</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">{t('repoDetail.retention.keepPerCoord')}</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">{t('repoDetail.retention.enabled')}</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">{t('repoDetail.retention.dryRun')}</th>
                  <th className="px-3 py-2 font-medium text-fg-muted">{t('repoDetail.retention.lastRun')}</th>
                  <th className="px-3 py-2 font-medium text-fg-muted"></th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.id ?? p.name} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-2 font-mono text-xs text-fg">{p.name}</td>
                    <td className="px-3 py-2 text-fg-muted text-xs">{p.rule.max_age_days ?? '—'}</td>
                    <td className="px-3 py-2 text-fg-muted text-xs">{p.rule.keep_per_coord ?? '—'}</td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={p.enabled}
                        disabled={busy}
                        onChange={(e) => void togglePolicy(p, { enabled: e.target.checked })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={p.dry_run}
                        disabled={busy}
                        onChange={(e) => void togglePolicy(p, { dry_run: e.target.checked })}
                      />
                    </td>
                    <td className="px-3 py-2 text-fg-muted text-xs whitespace-nowrap">
                      {p.last_run_at ? new Date(p.last_run_at).toLocaleString() : t('repoDetail.retention.never')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        disabled={busy}
                        onClick={() => void removePolicy(p)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-fg-muted text-sm mb-4">{t('repoDetail.retention.empty')}</p>
        )}

        <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-2">
            <label htmlFor="retention-policy-name" className="block text-xs text-fg-muted mb-1">{t('repoDetail.retention.name')}</label>
            <Input
              id="retention-policy-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder={t('repoDetail.retention.namePlaceholder')}
            />
          </div>
          <div>
            <label htmlFor="retention-policy-max-age" className="block text-xs text-fg-muted mb-1">{t('repoDetail.retention.maxAge')}</label>
            <Input
              id="retention-policy-max-age"
              type="number"
              min={1}
              value={draft.max_age_days}
              onChange={(e) => setDraft({ ...draft, max_age_days: e.target.value })}
              placeholder={t('repoDetail.retention.maxAgePlaceholder')}
            />
          </div>
          <div>
            <label htmlFor="retention-policy-keep-per-coord" className="block text-xs text-fg-muted mb-1">{t('repoDetail.retention.keepPerCoord')}</label>
            <Input
              id="retention-policy-keep-per-coord"
              type="number"
              min={1}
              value={draft.keep_per_coord}
              onChange={(e) => setDraft({ ...draft, keep_per_coord: e.target.value })}
              placeholder={t('repoDetail.retention.keepPlaceholder')}
            />
          </div>
          <div className="flex flex-col gap-2 text-xs text-fg-muted">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              />
              {' '}
              {t('repoDetail.retention.enabled')}
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.dry_run}
                onChange={(e) => setDraft({ ...draft, dry_run: e.target.checked })}
              />
              {' '}
              {t('repoDetail.retention.dryRun')}
            </label>
          </div>
          <div>
            <Button variant="primary" type="submit" disabled={busy}>
              {t('repoDetail.retention.addPolicy')}
            </Button>
          </div>
        </form>

        {formErr && <p className="text-danger text-xs mt-3">{formErr}</p>}
      </CardBody>
    </Card>
  );
}

// DockerImagesCard — roadmap item 137 Phase B. Replaces the generic
// artifact table (raw `manifests/<image>/sha256:<digest>` mirror
// paths) with a human-readable image → tags view for Docker/OCI
// format repos, backed by the Phase A endpoint
// `GET /api/v1/repos/{id}/docker/images`. Each tag row copies a ready
// -to-run `docker pull` command using the browser's own host, mirroring
// the pattern Tokens.tsx uses for CLI snippets.
function shortDigest(d: string): string {
  if (d.length <= 19) return d;
  return d.slice(0, 12) + '…' + d.slice(-4);
}

// Shared by DockerImagesCard and HelmOCIChartsCard, which both render a
// table of copy-to-clipboard pull commands keyed by row.
function useCopyToClipboard() {
  const [copied, setCopied] = useState<string | null>(null);
  const onCopy = async (key: string, cmd: string) => {
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard write can fail in non-secure contexts; the row still
      // shows the command text so the operator can select it manually.
    }
  };
  return { copied, onCopy };
}

function DockerImagesCard({
  repoID,
  projectSlug,
  repoSlug,
}: {
  readonly repoID: string;
  readonly projectSlug: string | undefined;
  readonly repoSlug: string;
}) {
  const { t } = useTranslation();
  const { items: images, error, isLoading, isValidating, hasMore, loadMore } =
    useDockerImagesPaginated(repoID);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const host = typeof globalThis === 'undefined' ? 'orbitalreg.example.com' : globalThis.location.host;

  const toggleExpanded = (imagePath: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(imagePath)) next.delete(imagePath);
      else next.add(imagePath);
      return next;
    });
  };

  return (
    <Card>
      <CardHeader
        title={`${t('repoDetail.docker.title', { count: images.length })}${hasMore ? '+' : ''}`}
        description={isLoading ? t('repoDetail.docker.loading') : undefined}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-5 py-3 font-medium text-fg-muted w-6"></th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.docker.image')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.docker.tagCount')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.docker.newestTag')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.docker.size')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.docker.updated')}</th>
            </tr>
          </thead>
          <tbody>
            {error && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-danger text-sm">
                  {String(error)}
                </td>
              </tr>
            )}
            {!error && images.length === 0 && !isLoading && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                  <Box className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                  {t('repoDetail.docker.emptyPrefix')}{' '}
                  <code className="orb-code">
                    docker push {host}/{projectSlug ?? '<project>'}/{repoSlug}/&lt;image&gt;:&lt;tag&gt;
                  </code>
                  {'.'}
                </td>
              </tr>
            )}
            {images.map((img) => (
              <DockerImageRow
                key={img.image_path}
                repoID={repoID}
                image={img}
                projectSlug={projectSlug}
                repoSlug={repoSlug}
                host={host}
                expanded={expanded.has(img.image_path)}
                onToggle={() => toggleExpanded(img.image_path)}
              />
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <CardBody>
          <div className="flex justify-center">
            <Button variant="ghost" onClick={loadMore} disabled={isValidating}>
              {isValidating ? <Spinner size="xs" /> : t('repoDetail.docker.showMoreImages')}
            </Button>
          </div>
        </CardBody>
      )}
    </Card>
  );
}

// DockerImageRow — one collapsed image-summary row (Item 302 Phase A).
// Expanding it mounts DockerImageTagRows, which fetches that image's
// tag page (Phase B) lazily so the per-tag manifest parse only ever
// runs for rows the user actually opens.
function DockerImageRow({
  repoID,
  image,
  projectSlug,
  repoSlug,
  host,
  expanded,
  onToggle,
}: {
  readonly repoID: string;
  readonly image: DockerImageSummary;
  readonly projectSlug: string | undefined;
  readonly repoSlug: string;
  readonly host: string;
  readonly expanded: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 cursor-pointer"
        onClick={onToggle}
      >
        <td className="px-5 py-3 text-fg-subtle">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md">
          {image.image_path}
        </td>
        <td className="px-5 py-3 text-fg-muted text-xs">{image.tag_count}</td>
        <td className="px-5 py-3">
          <Badge tone="accent">{image.newest_tag}</Badge>
        </td>
        <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
          {bytes(image.total_size_bytes)}
        </td>
        <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
          {new Date(image.last_push).toLocaleString()}
        </td>
      </tr>
      {expanded && (
        <DockerImageTagRows
          repoID={repoID}
          imagePath={image.image_path}
          projectSlug={projectSlug}
          repoSlug={repoSlug}
          host={host}
        />
      )}
    </>
  );
}

// DockerImageTagRows — Item 302 Phase B consumer. Renders the tag page
// for exactly one expanded image, with its own "show more" pagination
// so a single image with thousands of tags doesn't dump them all at once.
function DockerImageTagRows({
  repoID,
  imagePath,
  projectSlug,
  repoSlug,
  host,
}: {
  readonly repoID: string;
  readonly imagePath: string;
  readonly projectSlug: string | undefined;
  readonly repoSlug: string;
  readonly host: string;
}) {
  const { t } = useTranslation();
  const { items: tags, error, isLoading, isValidating, hasMore, loadMore } =
    useDockerImageTagsPaginated(repoID, imagePath, true);
  const { copied, onCopy } = useCopyToClipboard();

  const pullCommand = (tag: DockerImageTag) =>
    `docker pull ${host}/${projectSlug ?? '<project>'}/${repoSlug}/${imagePath}:${tag.tag}`;

  if (isLoading) {
    return (
      <tr className="bg-bg-hover/20">
        <td colSpan={6} className="px-5 py-3 text-center">
          <Spinner size="xs" />
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr className="bg-bg-hover/20">
        <td colSpan={6} className="px-5 py-3 text-center text-danger text-xs">
          {String(error)}
        </td>
      </tr>
    );
  }

  return (
    <>
      {tags.map((tag) => {
        const key = `${imagePath}:${tag.tag}`;
        const cmd = pullCommand(tag);
        return (
          <tr
            key={key}
            className="border-b border-border/60 last:border-0 hover:bg-bg-hover/60 cursor-pointer bg-bg-hover/20"
            onClick={() => onCopy(key, cmd)}
            title={t('repoDetail.docker.copyTitle')}
          >
            <td className="px-5 py-2"></td>
            <td className="px-5 py-2 pl-8">
              <Badge tone="accent">{tag.tag}</Badge>
            </td>
            <td className="px-5 py-2 text-fg-muted text-xs" colSpan={2}>
              <span className="orb-code text-[10px]" title={tag.digest}>
                {shortDigest(tag.digest)}
              </span>
            </td>
            <td className="px-5 py-2 text-fg-muted text-xs whitespace-nowrap">
              {bytes(tag.size_bytes)}
            </td>
            <td className="px-5 py-2 text-fg-muted text-xs whitespace-nowrap">
              {new Date(tag.updated_at).toLocaleString()}
              {copied === key ? ` · ${t('repoDetail.docker.copied')}` : ''}
            </td>
          </tr>
        );
      })}
      {hasMore && (
        <tr className="bg-bg-hover/20">
          <td colSpan={6} className="px-5 py-2 text-center">
            <Button variant="ghost" onClick={loadMore} disabled={isValidating}>
              {isValidating ? <Spinner size="xs" /> : t('repoDetail.docker.showMoreTags')}
            </Button>
          </td>
        </tr>
      )}
    </>
  );
}

// HelmOCIChartsCard — roadmap item 179 Phase C. `helm push oci://…`
// charts land in the same generic docker_tags/docker_manifests store
// as Docker images (Phase B), but the artifact-list panel below only
// reads from the `artifacts` table, so those charts were otherwise
// invisible in the repo browser for `helm`-format repos. This reuses
// the Phase A `GET /api/v1/repos/{id}/docker/images` endpoint (already
// format-agnostic) and renders it as chart name → versions, next to
// the still-live ChartMuseum `index.yaml`/`charts/` table below.
function HelmOCIChartsCard({
  repoID,
  projectSlug,
  repoSlug,
}: {
  readonly repoID: string;
  readonly projectSlug: string | undefined;
  readonly repoSlug: string;
}) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useDockerImages(repoID);
  const { copied, onCopy } = useCopyToClipboard();
  const host = typeof globalThis === 'undefined' ? 'orbitalreg.example.com' : globalThis.location.host;

  const pullCommand = (chartName: string, tag: DockerImageTag) =>
    `helm pull oci://${host}/${projectSlug ?? '<project>'}/${repoSlug}/${chartName} --version ${tag.tag}`;

  const charts = data?.images ?? [];
  const versionCount = charts.reduce((n, c) => n + c.tags.length, 0);
  if (!isLoading && !error && charts.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title={t('repoDetail.helm.title', { count: charts.length })}
        description={
          isLoading
            ? t('repoDetail.helm.loading')
            : t('repoDetail.helm.description', { count: versionCount })
        }
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.helm.chart')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.helm.version')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.helm.size')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.helm.digest')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.helm.updated')}</th>
              <th className="px-5 py-3 font-medium text-fg-muted w-10"></th>
            </tr>
          </thead>
          <tbody>
            {error && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-danger text-sm">
                  {String(error)}
                </td>
              </tr>
            )}
            {charts.map((c) =>
              c.tags.map((tag) => {
                const key = `${c.image_path}:${tag.tag}`;
                const cmd = pullCommand(c.image_path, tag);
                return (
                  <tr
                    key={key}
                    className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40 cursor-pointer"
                    onClick={() => onCopy(key, cmd)}
                    title={t('repoDetail.helm.copyTitle')}
                  >
                    <td className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md">
                      {c.image_path}
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone="accent">{tag.tag}</Badge>
                      {tag.has_provenance && (
                        <span
                          className="inline-flex items-center gap-1 ml-2 text-fg-muted text-xs"
                          title={t('repoDetail.helm.provenanceTitle')}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                          {t('repoDetail.helm.provenance')}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                      {bytes(tag.size_bytes)}
                    </td>
                    <td className="px-5 py-3">
                      <span className="orb-code text-[10px]" title={tag.digest}>
                        {shortDigest(tag.digest)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                      {new Date(tag.updated_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-fg-subtle text-xs">
                        <Copy className="h-3.5 w-3.5" />
                        {copied === key ? t('repoDetail.helm.copied') : ''}
                      </span>
                    </td>
                  </tr>
                );
              }),
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// StatsTab — v190. Renders the per-repo stats dashboard for the
// "Stats" tab on RepoDetail. Talks to /api/v1/repos/{id}/stats and
// renders four blocks:
//   1. Tile row (storage total / 30-d growth / artifact count / total pulls)
//   2. Daily-pulls SVG sparkline (line + value labels on hover-tooltips)
//   3. Top consumers bar list (only populated for maintainer+ callers)
//   4. Top artifacts by pull count table
// We render charts with hand-rolled SVG instead of pulling in a
// charting library — the dataset is fixed-size (30 daily buckets,
// up to 10 consumers, up to 20 artifacts) and the visual budget is
// "operator skim", not "BI dashboard". Adding recharts to satisfy
// this surface alone would be a 200 KiB JS payload + a build-time
// dependency to maintain; sparkline + bar-chart in raw SVG is
// shorter than the import statement would be.
function StatsTab({ repoID }: { readonly repoID: string }) {
  const { t } = useTranslation();
  const { data, error, isLoading } = useRepoStats(repoID);

  if (error) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted text-sm">
            {t('repoDetail.stats.unavailable')}
          </p>
        </CardBody>
      </Card>
    );
  }
  if (isLoading || !data) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted text-sm">{t('repoDetail.stats.loading')}</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title={t('repoDetail.stats.activityTitle')}
          description={t('repoDetail.stats.activityDescription')}
        />
        <CardBody>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatTile label={t('repoDetail.stats.totalPulls')} value={data.pulls_total_30d.toLocaleString()} />
            <StatTile label={t('repoDetail.stats.totalStorage')} value={bytes(data.storage_total_bytes)} />
            <StatTile label={t('repoDetail.stats.storageGrowth')} value={bytes(data.storage_growth_30d)} />
            <StatTile label={t('repoDetail.stats.artifactCount')} value={data.artifact_count.toLocaleString()} />
          </div>
          <PullsSparkline daily={data.pulls_30d_daily} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('repoDetail.stats.topConsumersTitle')}
          description={t('repoDetail.stats.topConsumersDescription')}
        />
        <CardBody>
          {data.top_consumers.length === 0 ? (
            <p className="text-fg-muted text-sm">
              {t('repoDetail.stats.noConsumers')}
            </p>
          ) : (
            <ConsumerBars consumers={data.top_consumers} />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('repoDetail.stats.topArtifactsTitle')}
          description={t('repoDetail.stats.topArtifactsDescription')}
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.stats.path')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.stats.version')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.stats.size')}</th>
                <th className="px-5 py-3 font-medium text-fg-muted text-right">{t('repoDetail.stats.pulls')}</th>
              </tr>
            </thead>
            <tbody>
              {data.top_artifacts_by_pulls.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-fg-muted text-sm">
                    {t('repoDetail.stats.noPulls')}
                  </td>
                </tr>
              ) : (
                data.top_artifacts_by_pulls.map((a) => (
                  <tr key={a.artifact_id} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md">{a.path}</td>
                    <td className="px-5 py-3 text-fg text-xs">{a.version || '—'}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">{bytes(a.size_bytes)}</td>
                    <td className="px-5 py-3 text-fg text-xs text-right font-mono">{a.pull_count.toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatTile({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <p className="orb-section-title text-fg-muted">{label}</p>
      <p className="text-2xl font-display text-fg mt-1">{value}</p>
    </div>
  );
}

// PullsSparkline — fixed 30-bucket time series rendered as a
// connected polyline over a faint baseline grid. We size off a
// known viewBox (700x140) and let the parent container scale it via
// width=100% so the chart adapts to the page width without
// recomputing on resize.
function xLabelAnchor(idx: number, count: number): 'start' | 'end' | 'middle' {
  if (idx === 0) return 'start';
  if (idx === count - 1) return 'end';
  return 'middle';
}

function PullsSparkline({ daily }: { readonly daily: RepoStats['pulls_30d_daily'] }) {
  const { t } = useTranslation();
  if (!daily.length) {
    return <p className="text-fg-muted text-sm">{t('repoDetail.stats.noPullData')}</p>;
  }
  const W = 700;
  const H = 140;
  const PAD_L = 28;
  const PAD_R = 8;
  const PAD_T = 12;
  const PAD_B = 24;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const max = Math.max(1, ...daily.map((d) => d.count));
  const stepX = daily.length > 1 ? innerW / (daily.length - 1) : 0;

  const points = daily
    .map((d, i) => {
      const x = PAD_L + i * stepX;
      const y = PAD_T + innerH - (d.count / max) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  // Y-axis labels: show 0, mid, max for orientation. The dataset is
  // small enough that a 3-tick axis communicates the magnitude
  // without crowding.
  const ticks = [0, Math.round(max / 2), max];

  // X-axis labels: show first, middle, last day. More than three
  // makes the labels overlap on narrow viewports.
  const xLabels = daily.length >= 3
    ? [daily[0], daily[Math.floor(daily.length / 2)], daily[daily.length - 1]]
    : daily;

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-40 text-accent">
        {/* baseline grid lines */}
        {ticks.map((tick, idx) => {
          const y = PAD_T + innerH - (tick / max) * innerH;
          return (
            <g key={`${tick}-${idx}`}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={y}
                y2={y}
                stroke="currentColor"
                strokeOpacity={0.12}
                strokeWidth={1}
              />
              <text
                x={PAD_L - 6}
                y={y + 3}
                textAnchor="end"
                fontSize={10}
                fill="currentColor"
                fillOpacity={0.5}
              >
                {tick}
              </text>
            </g>
          );
        })}
        {/* connecting line + filled area for visual weight */}
        <polyline
          points={points}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <polygon
          points={`${PAD_L},${PAD_T + innerH} ${points} ${PAD_L + (daily.length - 1) * stepX},${PAD_T + innerH}`}
          fill="currentColor"
          fillOpacity={0.08}
        />
        {/* day-of-window labels along the bottom */}
        {xLabels.map((d, idx) => {
          const i = daily.indexOf(d);
          const x = PAD_L + i * stepX;
          const anchor = xLabelAnchor(idx, xLabels.length);
          return (
            <text
              key={d.day}
              x={x}
              y={H - 6}
              textAnchor={anchor}
              fontSize={10}
              fill="currentColor"
              fillOpacity={0.5}
            >
              {d.day.slice(5)}
            </text>
          );
        })}
        {/* per-point dots — small to avoid clutter when most points are flat */}
        {daily.map((d, i) => {
          const x = PAD_L + i * stepX;
          const y = PAD_T + innerH - (d.count / max) * innerH;
          return (
            <circle key={d.day} cx={x} cy={y} r={2} fill="currentColor">
              <title>{t('repoDetail.stats.pullsTooltip', { day: d.day, count: d.count })}</title>
            </circle>
          );
        })}
      </svg>
    </div>
  );
}

// ConsumerBars — horizontal bar list. Each row is a labelled
// `principal_email` with a proportional bar (max-width = top
// puller's count). Service-account principals get a neutral
// "service" badge so the operator can tell apart "a CI runner
// pulling on every build" vs. "a developer doing manual installs".
function ConsumerBars({ consumers }: { readonly consumers: RepoStats['top_consumers'] }) {
  const { t } = useTranslation();
  const max = Math.max(1, ...consumers.map((c) => c.count));
  return (
    <div className="space-y-2">
      {consumers.map((c) => {
        const pct = Math.round((c.count / max) * 100);
        return (
          <div key={`${c.principal_email}-${c.principal_kind}`} className="flex items-center gap-3">
            <div className="w-56 truncate text-xs text-fg" title={c.principal_email}>
              {c.principal_email}
              {c.principal_kind === 'service_account' && (
                <span className="ml-2 text-[10px] uppercase text-fg-muted">{t('repoDetail.stats.sa')}</span>
              )}
              {c.principal_kind === 'anonymous' && (
                <span className="ml-2 text-[10px] uppercase text-fg-muted">{t('repoDetail.stats.anon')}</span>
              )}
            </div>
            <div className="flex-1 h-3 bg-bg-inset rounded-sm overflow-hidden">
              <div
                className="h-full bg-accent/70"
                style={{ width: `${pct}%` }}
                title={t('repoDetail.stats.countTooltip', { count: c.count })}
              />
            </div>
            <div className="w-16 text-right text-xs text-fg font-mono">{c.count}</div>
          </div>
        );
      })}
    </div>
  );
}

// ProvenanceGraph — roadmap item 323 Phase B. One node per member repo
// plus the virtual repo itself — node count is bounded by the member
// list, never by artifact count (the lesson item 301 learned the hard
// way for the Docker image view, where DOM rows tracked the tag count
// instead of the image count). Edge width encodes how many coordinates
// a member wins. Shadowing — a member's coordinate losing to a
// higher-priority member — is a distinct dashed edge, drawn only for
// the expanded member so the graph doesn't turn into a hairball when
// several members shadow each other.
function ProvenanceGraph({
  members,
  parentSlug,
  repoLabel,
  expandedId,
  onToggle,
  onEdgeClick,
}: {
  readonly members: RepoResolutionStatsMember[];
  readonly parentSlug: string;
  readonly repoLabel: (id: string) => string;
  readonly expandedId: string | null;
  readonly onToggle: (id: string) => void;
  readonly onEdgeClick: (memberId: string, status: 'won' | 'shadowed') => void;
}) {
  const { t } = useTranslation();
  const ROW_H = 56;
  const W = 720;
  const H = Math.max(120, members.length * ROW_H + 24);
  const parentX = 90;
  const parentY = H / 2;
  const memberX = W - 120;
  const maxWon = Math.max(1, ...members.map((m) => m.won_count));
  const memberY = (i: number) => 12 + i * ROW_H + ROW_H / 2;

  const expandedIdx = expandedId ? members.findIndex((m) => m.repo_id === expandedId) : -1;
  const shadowEdges =
    expandedIdx >= 0
      ? members[expandedIdx].shadowed_by
          .map((sb) => ({ toIdx: members.findIndex((m) => m.repo_id === sb.repo_id), count: sb.count }))
          .filter((e) => e.toIdx >= 0)
      : [];

  const label = (id: string) => {
    const slug = repoLabel(id);
    return slug.length > 16 ? `${slug.slice(0, 15)}…` : slug;
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full"
      style={{ height: H }}
      role="img"
      aria-label={t('repoDetail.provenance.graphLabel')}
    >
      {members.map((m, i) => {
        const y = memberY(i);
        const strokeW = 1.5 + (m.won_count / maxWon) * 6.5;
        const path = `M ${parentX + 58} ${parentY} C ${(parentX + memberX) / 2} ${parentY}, ${(parentX + memberX) / 2} ${y}, ${memberX - 58} ${y}`;
        return (
          <path
            key={`won-${m.repo_id}`}
            d={path}
            fill="none"
            stroke="rgb(var(--orb-accent))"
            strokeOpacity={0.5}
            strokeWidth={strokeW}
            strokeLinecap="round"
            style={{ cursor: m.won_count > 0 ? 'pointer' : undefined }}
            role={m.won_count > 0 ? 'button' : undefined}
            tabIndex={m.won_count > 0 ? 0 : undefined}
            onClick={() => m.won_count > 0 && onEdgeClick(m.repo_id, 'won')}
            onKeyDown={(e) => {
              if (m.won_count > 0 && (e.key === 'Enter' || e.key === ' ')) onEdgeClick(m.repo_id, 'won');
            }}
          >
            <title>
              {t('repoDetail.provenance.wonEdgeTooltip', { slug: repoLabel(m.repo_id), count: m.won_count })}
            </title>
          </path>
        );
      })}

      {shadowEdges.map((e) => {
        const y1 = memberY(expandedIdx);
        const y2 = memberY(e.toIdx);
        const path = `M ${memberX - 40} ${y1} C ${memberX + 70} ${y1}, ${memberX + 70} ${y2}, ${memberX - 40} ${y2}`;
        return (
          <path
            key={`shadow-${e.toIdx}`}
            d={path}
            fill="none"
            stroke="rgb(var(--orb-danger))"
            strokeWidth={2}
            strokeDasharray="4 3"
            strokeLinecap="round"
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onClick={() => expandedId && onEdgeClick(expandedId, 'shadowed')}
            onKeyDown={(ev) => {
              if (expandedId && (ev.key === 'Enter' || ev.key === ' ')) onEdgeClick(expandedId, 'shadowed');
            }}
          >
            <title>
              {t('repoDetail.provenance.shadowEdgeTooltip', {
                count: e.count,
                winner: repoLabel(members[e.toIdx].repo_id),
              })}
            </title>
          </path>
        );
      })}

      <g>
        <rect
          x={parentX - 58}
          y={parentY - 20}
          width={116}
          height={40}
          rx={8}
          fill="rgb(var(--orb-bg-inset))"
          stroke="rgb(var(--orb-border-strong))"
        />
        <text x={parentX} y={parentY + 4} textAnchor="middle" fontSize={11} fill="rgb(var(--orb-fg))" fontWeight={600}>
          {parentSlug.length > 16 ? `${parentSlug.slice(0, 15)}…` : parentSlug}
        </text>
      </g>

      {members.map((m, i) => {
        const y = memberY(i);
        const isExpanded = m.repo_id === expandedId;
        return (
          <g
            key={`node-${m.repo_id}`}
            onClick={() => onToggle(m.repo_id)}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onToggle(m.repo_id);
            }}
          >
            <rect
              x={memberX - 58}
              y={y - 20}
              width={116}
              height={40}
              rx={8}
              fill="rgb(var(--orb-bg-elevated))"
              stroke={isExpanded ? 'rgb(var(--orb-accent))' : 'rgb(var(--orb-border))'}
              strokeWidth={isExpanded ? 2 : 1}
            />
            <text x={memberX - 48} y={y - 4} fontSize={9} fill="rgb(var(--orb-fg-subtle))">
              #{m.priority + 1}
            </text>
            <text x={memberX} y={y + 6} textAnchor="middle" fontSize={11} fill="rgb(var(--orb-fg))">
              {label(m.repo_id)}
            </text>
            {m.shadowed_count > 0 && (
              <>
                <circle cx={memberX + 50} cy={y - 16} r={8} fill="rgb(var(--orb-danger))" />
                <text
                  x={memberX + 50}
                  y={y - 13}
                  textAnchor="middle"
                  fontSize={8}
                  fill="rgb(var(--orb-accent-fg))"
                  pointerEvents="none"
                >
                  {m.shadowed_count > 9 ? '9+' : m.shadowed_count}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ProvenanceTab — item 323 Phase B. Backend data comes from
// useRepoResolutionStats (Phase A endpoint); repo id → slug labels
// come from the already-cached useRepos() list (same source
// MemberSelector uses), so no extra round-trip per member.
function ProvenanceTab({ repo }: { readonly repo: Repo }) {
  const { t } = useTranslation();
  const { data, error, isLoading } = useRepoResolutionStats(repo.id, repo.kind);
  const { data: allRepos } = useRepos();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [drillDown, setDrillDown] = useState<{ memberId: string; status: 'won' | 'shadowed' } | null>(null);

  const repoLabel = (id: string) => allRepos?.find((r) => r.id === id)?.slug ?? id;

  if (error) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted text-sm">{t('repoDetail.provenance.unavailable')}</p>
        </CardBody>
      </Card>
    );
  }
  if (isLoading || !data) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted text-sm">{t('repoDetail.provenance.loading')}</p>
        </CardBody>
      </Card>
    );
  }

  const members = [...data.members].sort((a, b) => a.priority - b.priority);
  const totalShadowed = members.reduce((s, m) => s + m.shadowed_count, 0);
  const toggle = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title={t('repoDetail.provenance.title')} description={t('repoDetail.provenance.description')} />
        <CardBody>
          {members.length === 0 ? (
            <p className="text-fg-muted text-sm">{t('repoDetail.provenance.empty')}</p>
          ) : (
            <>
              <ProvenanceGraph
                members={members}
                parentSlug={repo.slug}
                repoLabel={repoLabel}
                expandedId={expandedId}
                onToggle={toggle}
                onEdgeClick={(memberId, status) => setDrillDown({ memberId, status })}
              />
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-fg-muted">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-0.5 w-4 bg-accent" />
                  {t('repoDetail.provenance.legendWon')}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-0 w-4 border-t-2 border-dashed border-danger" />
                  {t('repoDetail.provenance.legendShadowed')}
                </span>
                {totalShadowed > 0 && (
                  <span>{t('repoDetail.provenance.totalShadowed', { count: totalShadowed })}</span>
                )}
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {members.length > 0 && (
        <Card>
          <CardHeader
            title={t('repoDetail.provenance.membersTitle')}
            description={t('repoDetail.provenance.membersDescription')}
          />
          <div className="divide-y divide-border">
            {members.map((m) => {
              const isExpanded = m.repo_id === expandedId;
              return (
                <div key={m.repo_id}>
                  <button
                    type="button"
                    onClick={() => toggle(m.repo_id)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-bg-hover/40"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-fg-muted" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-fg-muted" />
                    )}
                    <span className="text-xs text-fg-subtle tabular-nums w-6">#{m.priority + 1}</span>
                    <span className="font-mono text-xs text-accent flex-1 truncate">{repoLabel(m.repo_id)}</span>
                    <span className="text-xs text-fg-muted">
                      {t('repoDetail.provenance.wonCount', { count: m.won_count })}
                    </span>
                    {m.shadowed_count > 0 ? (
                      <Badge tone="danger">
                        {t('repoDetail.provenance.shadowedCount', { count: m.shadowed_count })}
                      </Badge>
                    ) : (
                      <span className="text-xs text-fg-subtle">{t('repoDetail.provenance.noneShadowed')}</span>
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-5 pb-3 pl-14 space-y-2">
                      {m.shadowed_by.length > 0 && (
                        <div className="space-y-1">
                          {m.shadowed_by.map((sb) => (
                            <p key={sb.repo_id} className="text-xs text-fg-muted">
                              {t('repoDetail.provenance.shadowedByRow', {
                                count: sb.count,
                                slug: repoLabel(sb.repo_id),
                              })}
                            </p>
                          ))}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {m.won_count > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDrillDown({ memberId: m.repo_id, status: 'won' })}
                          >
                            {t('repoDetail.provenance.viewWonCoordinates', { count: m.won_count })}
                          </Button>
                        )}
                        {m.shadowed_count > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDrillDown({ memberId: m.repo_id, status: 'shadowed' })}
                          >
                            {t('repoDetail.provenance.viewShadowedCoordinates', { count: m.shadowed_count })}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {drillDown && (
        <ResolutionCoordinatesPanel
          repoId={repo.id}
          memberId={drillDown.memberId}
          status={drillDown.status}
          memberLabel={repoLabel(drillDown.memberId)}
          repoLabel={repoLabel}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
}

// ResolutionCoordinatesPanel — item 323 Phase C. The concrete (path,
// version) coordinates behind one provenance-graph edge, keyset-paginated
// the same way DockerImageTagRows pages a single image's tags: never all
// loaded at once, since a virtual repo over large members can have
// hundreds of thousands of coordinates.
function ResolutionCoordinatesPanel({
  repoId,
  memberId,
  status,
  memberLabel,
  repoLabel,
  onClose,
}: {
  readonly repoId: string;
  readonly memberId: string;
  readonly status: 'won' | 'shadowed';
  readonly memberLabel: string;
  readonly repoLabel: (id: string) => string;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { items, error, isLoading, isValidating, hasMore, loadMore } = useRepoResolutionCoordinates(
    repoId,
    memberId,
    status,
  );

  return (
    <Card>
      <CardHeader
        title={t(
          status === 'won' ? 'repoDetail.provenance.coordinates.wonTitle' : 'repoDetail.provenance.coordinates.shadowedTitle',
          { slug: memberLabel },
        )}
        actions={
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('repoDetail.provenance.coordinates.close')}>
            <X className="h-4 w-4" />
          </Button>
        }
      />
      <div className="divide-y divide-border">
        {isLoading && (
          <p className="px-5 py-4 text-sm text-fg-muted">{t('repoDetail.provenance.coordinates.loading')}</p>
        )}
        {error && (
          <p className="px-5 py-4 text-sm text-danger">{t('repoDetail.provenance.coordinates.error')}</p>
        )}
        {!isLoading && !error && items.length === 0 && (
          <p className="px-5 py-4 text-sm text-fg-muted">{t('repoDetail.provenance.coordinates.empty')}</p>
        )}
        {items.map((c) => (
          <div key={`${c.path}@${c.version ?? ''}`} className="px-5 py-2.5 flex items-center gap-3 text-xs">
            <span className="font-mono text-accent flex-1 truncate">
              {c.path}
              {c.version ? `@${c.version}` : ''}
            </span>
            {c.winner_repo_id && (
              <Badge tone="danger">
                {t('repoDetail.provenance.coordinates.wonBy', { slug: repoLabel(c.winner_repo_id) })}
              </Badge>
            )}
            <span className="text-fg-subtle whitespace-nowrap">{new Date(c.uploaded_at).toLocaleString()}</span>
          </div>
        ))}
      </div>
      {hasMore && (
        <div className="px-5 py-3 text-center border-t border-border">
          <Button variant="ghost" size="sm" onClick={loadMore} disabled={isValidating}>
            {isValidating ? <Spinner size="xs" /> : t('repoDetail.provenance.coordinates.showMore')}
          </Button>
        </div>
      )}
    </Card>
  );
}

interface RepoHeaderProps {
  readonly loadingRepo: boolean;
  readonly repo: Repo | undefined;
  readonly editing: boolean;
  readonly onSaved: (next: Repo) => void;
  readonly onCancel: () => void;
  readonly onEdit: () => void;
  readonly onTrash: () => void;
}

function RepoHeader({ loadingRepo, repo, editing, onSaved, onCancel, onEdit, onTrash }: RepoHeaderProps) {
  const { t } = useTranslation();
  if (loadingRepo) {
    return (
      <Card>
        <CardBody>
          <p className="text-fg-muted">{t('repoDetail.loading')}</p>
        </CardBody>
      </Card>
    );
  }
  if (!repo) return null;
  if (editing) {
    return <EditForm repo={repo} onSaved={onSaved} onCancel={onCancel} />;
  }
  return <ReadOnlyHeader repo={repo} onEdit={onEdit} onTrash={onTrash} />;
}

type Tab = 'contents' | 'stats' | 'provenance';

function isTab(v: string | null): v is Tab {
  return v === 'contents' || v === 'stats' || v === 'provenance';
}

export function RepoDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: repo, isLoading: loadingRepo, mutate } = useRepoDetail(id);
  const { data: artifacts, isLoading: loadingArts } = useRepoArtifacts(id, 500);
  const [editing, setEditing] = useState(false);

  // Docker-format repos get a readable image/tag view in the Contents
  // tab instead of the raw `manifests/<image>/sha256:<digest>` mirror
  // path (roadmap item 137). projectSlug is resolved the same way the
  // Ollama/Kustomize/OPA browsers do it — from the cached project list,
  // keyed off repo.projectId — since the pull command needs slugs, not
  // the repo UUID.
  const { data: projects } = useProjects();
  const projectSlug = useMemo(
    () => projects?.find((p) => p.id === repo?.projectId)?.slug,
    [projects, repo?.projectId],
  );

  // Move the repo to trash (soft-delete). Recoverable from /trash for 30 days;
  // the backend enforces project_admin and 403s otherwise.
  const onTrash = async () => {
    if (!repo) return;
    if (!confirm(t('repoDetail.trash.confirm', { name: repo.name }))) return;
    try {
      await deleteRepo(repo.id);
      navigate('/repos');
    } catch (e) {
      alert(t('repoDetail.trash.error', { message: e instanceof Error ? e.message : String(e) }));
    }
  };

  // Tab state lives in `?tab=` so /repos/<id>?tab=stats is shareable
  // and the browser back-button toggles between the two views.
  // 'contents' is the default; we drop the param when landing on it
  // to keep the URL clean.
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const requestedTab: Tab = isTab(rawTab) ? rawTab : 'contents';
  // The provenance tab only exists for virtual repos (the backend 400s
  // the resolution-stats endpoint for anything else) — a stale deep
  // link to ?tab=provenance on a local/remote repo falls back to
  // contents instead of rendering a blank pane.
  const activeTab: Tab = requestedTab === 'provenance' && repo && repo.kind !== 'virtual' ? 'contents' : requestedTab;
  const setTab = (tab: Tab) => {
    if (tab === 'contents') setParams({}, { replace: false });
    else setParams({ tab }, { replace: false });
  };

  return (
    <div className="space-y-6">
      <Link to="/repos" className="inline-flex items-center gap-2 text-fg-muted hover:text-fg text-sm">
        <ArrowLeft className="h-4 w-4" />
        {t('repoDetail.back')}
      </Link>

      <RepoHeader
        loadingRepo={loadingRepo}
        repo={repo}
        editing={editing}
        onSaved={(next) => {
          // SWR's mutate accepts the new value directly; pass it
          // so the page re-renders with the saved row without a
          // round-trip.
          void mutate(next);
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
        onEdit={() => setEditing(true)}
        onTrash={onTrash}
      />

      {repo && !editing && (
        <div role="tablist" className="flex flex-wrap items-center gap-2 border-b border-border pb-2">
          <TabButton
            label={t('repoDetail.tabs.contents')}
            Icon={FolderOpen}
            active={activeTab === 'contents'}
            onClick={() => setTab('contents')}
          />
          <TabButton
            label={t('repoDetail.tabs.stats')}
            Icon={BarChart3}
            active={activeTab === 'stats'}
            onClick={() => setTab('stats')}
          />
          {/* Provenance graph (roadmap item 323) — only virtual repos
              have a member overlay to resolve/shadow, so the tab is
              hidden for local/remote repos rather than shown disabled. */}
          {repo.kind === 'virtual' && (
            <TabButton
              label={t('repoDetail.tabs.provenance')}
              Icon={GitBranch}
              active={activeTab === 'provenance'}
              onClick={() => setTab('provenance')}
            />
          )}
          {/* Ollama-format repos get a deep-link to the per-model
              browser (roadmap item 38). The dedicated page renders the
              manifest + per-layer breakdown that's compliance-relevant
              for AI bundles. */}
          {repo.format === 'ollama' && (
            <Link
              to={`/repos/${repo.id}/ollama`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              {t('repoDetail.tabs.models')}
            </Link>
          )}
          {/* Kustomize-format repos get a deep-link to the per-component
              browser (roadmap item 43). Renders the manifest + per-layer
              breakdown plus a kustomization.yaml preview. */}
          {repo.format === 'kustomize' && (
            <Link
              to={`/repos/${repo.id}/kustomize`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              {t('repoDetail.tabs.components')}
            </Link>
          )}
          {/* OPA-format repos get a deep-link to the per-bundle browser
              (roadmap item 45). Renders bundle catalog + Rego/data preview. */}
          {repo.format === 'opa' && (
            <Link
              to={`/repos/${repo.id}/opa`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              {t('repoDetail.tabs.bundles')}
            </Link>
          )}
          {/* Agent/Skill-format repos get a deep-link to the package
              browser (roadmap item 149 Phase B). Renders manifest +
              tools/permissions/egress + SKILL.md per package. */}
          {repo.format === 'agentskill' && (
            <Link
              to={`/repos/${repo.id}/agentskill`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              {t('repoDetail.tabs.packages')}
            </Link>
          )}
          {repo.format === 'mcp' && (
            <Link
              to={`/repos/${repo.id}/mcp`}
              className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            >
              <Box className="h-4 w-4" />
              {t('repoDetail.tabs.mcpServers')}
            </Link>
          )}
        </div>
      )}

      {repo && !editing && activeTab === 'contents' && (
        <>
          {repo.kind === 'virtual' && <SafeVersionCard repoID={repo.id} />}
          {repo.kind === 'virtual' && <RepoSigningKeyCard repoID={repo.id} format={repo.format} />}
          {repo.kind === 'remote' && <RepoReleaseHoldCard repoID={repo.id} />}
          <RetentionCard repoID={repo.id} />

          {artifacts && artifacts.length > 0 && (
            <Card>
              <CardHeader title={t('repoDetail.quickStats.title')} />
              <CardBody>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="orb-section-title text-fg-muted">{t('repoDetail.quickStats.totalArtifacts')}</p>
                    <p className="text-2xl font-display text-fg mt-1">{artifacts.length}</p>
                  </div>
                  <div>
                    <p className="orb-section-title text-fg-muted">{t('repoDetail.quickStats.totalSize')}</p>
                    <p className="text-2xl font-display text-fg mt-1">
                      {bytes(artifacts.reduce((s, a) => s + a.size_bytes, 0))}
                    </p>
                  </div>
                  <div>
                    <p className="orb-section-title text-fg-muted">{t('repoDetail.quickStats.uniqueVersions')}</p>
                    <p className="text-2xl font-display text-fg mt-1">
                      {new Set(artifacts.map((a) => a.version).filter(Boolean)).size}
                    </p>
                  </div>
                  <div>
                    <p className="orb-section-title text-fg-muted">{t('repoDetail.quickStats.lastUpload')}</p>
                    <p className="text-sm text-fg mt-1 flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-fg-muted" />
                      {new Date(artifacts[0].uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {repo.format === 'helm' && (
            <HelmOCIChartsCard repoID={repo.id} projectSlug={projectSlug} repoSlug={repo.slug} />
          )}

          {repo.format === 'docker' ? (
            <DockerImagesCard repoID={repo.id} projectSlug={projectSlug} repoSlug={repo.slug} />
          ) : (
            <Card>
              <CardHeader
                title={t('repoDetail.artifacts.title', { count: artifacts?.length ?? 0 })}
                description={loadingArts ? t('repoDetail.artifacts.loading') : t('repoDetail.artifacts.description')}
              />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.artifacts.path')}</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.artifacts.version')}</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.artifacts.size')}</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.artifacts.sha256')}</th>
                      <th className="px-5 py-3 font-medium text-fg-muted">{t('repoDetail.artifacts.uploaded')}</th>
                      {/* v193 — per-artifact detail page; deps tree among other tabs. */}
                      <th className="px-5 py-3 font-medium text-fg-muted w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifacts?.length ? (
                      artifacts.map((a) => (
                        <tr
                          key={a.id}
                          className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40"
                        >
                          <td
                            className="px-5 py-3 font-mono text-xs text-fg break-all max-w-md cursor-pointer"
                            onClick={() => navigator.clipboard.writeText(a.path)}
                            title={t('repoDetail.artifacts.copyTitle')}
                          >
                            {a.path}
                          </td>
                          <td className="px-5 py-3 text-fg text-xs">{a.version || '—'}</td>
                          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">{bytes(a.size_bytes)}</td>
                          <td className="px-5 py-3">
                            <span className="orb-code text-[10px]" title={a.sha256}>
                              {a.sha256.slice(0, 12)}…
                            </span>
                          </td>
                          <td className="px-5 py-3 text-fg-muted text-xs whitespace-nowrap">
                            {new Date(a.uploaded_at).toLocaleString()}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Link
                              to={`/artifacts/${a.id}`}
                              className="inline-flex items-center gap-1 text-fg-subtle hover:text-accent text-xs"
                              title={t('repoDetail.artifacts.depsTitle')}
                            >
                              <GitBranch className="h-3.5 w-3.5" />
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                          <Download className="h-8 w-8 mx-auto mb-2 text-fg-subtle" />
                          {t('repoDetail.artifacts.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {repo && !editing && activeTab === 'stats' && <StatsTab repoID={repo.id} />}

      {repo && !editing && activeTab === 'provenance' && repo.kind === 'virtual' && <ProvenanceTab repo={repo} />}
    </div>
  );
}

function TabButton({ label, Icon, active, onClick }: {
  readonly label: string;
  readonly Icon: typeof FolderOpen;
  readonly active: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ' +
        (active
          ? 'bg-bg-elevated text-fg border border-border'
          : 'text-fg-muted hover:text-fg hover:bg-bg-elevated/60')
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}
